



import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaEdit, FaSave } from "react-icons/fa";

// Inline loader hook (same as Userdetails component)
const useInlineLoader = () => {
    const [loading, setLoading] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);

    const showLoader = () => {
        setFadeOut(false);
        setLoading(true);
    };

    const hideLoader = () => {
        setFadeOut(true);
        setTimeout(() => {
            setLoading(false);
            setFadeOut(false);
        }, 500);
    };

    return { showLoader, hideLoader, loading, fadeOut };
};

// Loader Component (same as Userdetails component)
const Loader = ({ loading, fadeOut, text = "Loading..." }) => {
    if (!loading) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            transition: 'opacity 0.4s ease',
            fontFamily: 'Arial, sans-serif',
            opacity: fadeOut ? 0 : 1,
            pointerEvents: fadeOut ? 'none' : 'auto'
        }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{
                    fontSize: '2rem',
                    fontWeight: 900,
                    letterSpacing: '6px',
                    marginBottom: '30px',
                }}>
                    {"MAAMAAS".split("").map((l, i) => (
                        <span
                            key={i}
                            style={{
                                display: 'inline-block',
                                opacity: 0,
                                transform: 'translateY(30px)',
                                animation: `letterAnim 0.8s ease forwards ${i * 0.1}s`,
                                color: '#e50914',
                            }}
                        >
                            {l}
                        </span>
                    ))}
                </div>

                <div style={{
                    width: '280px',
                    height: '4px',
                    background: 'rgba(255,255,255,0.2)',
                    overflow: 'hidden',
                    margin: '0 auto',
                }}>
                    <div style={{
                        width: '40%',
                        height: '100%',
                        background: 'linear-gradient(90deg,#e50914,#ff6b6b)',
                        animation: 'progress 1.8s infinite',
                    }}></div>
                </div>

                <div style={{
                    marginTop: '20px',
                    color: '#aaa',
                    fontSize: '14px',
                }}>
                    {text}
                </div>
            </div>
        </div>
    );
};

const Cateringbillingsetup = () => {
    const billingFields = [
        { label: "Commission", key: "commission" },
        { label: "Commission GST", key: "commissionGst" },
        { label: "GST", key: "gst" },
        { label: "Platform Fee", key: "platformFee" },
        { label: "Lead Price", key: "leadPercentage" },
        { label: "Delivery Fee per km", key: "deliveryFeeperThousandperKm" },
        { label: "Partial Payment", key: "partialPayment" },
        { label: "TopRated Fee", key: "topRated" },
    ];

    const [billingData, setBillingData] = useState({});
    const [editMode, setEditMode] = useState({});
    const [loading, setLoading] = useState(false);

    // Use inline loader (same as Userdetails component)
    const { showLoader, hideLoader, loading: globalLoading, fadeOut } = useInlineLoader();

    useEffect(() => {
        fetchBillingData();
    }, []);

    const fetchBillingData = async () => {
        try {
            showLoader(); // Show global loader when fetching starts

            const token = localStorage.getItem("authToken");
            if (!token) {
                console.error("❌ Auth token not found in localStorage");
                hideLoader();
                return;
            }

            const url =
                "https://backend.maamaas.com/catering/api/admin/billing/get";
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = response.data || {};
            console.log("📦 Billing API response:", data);

            const initializedData = billingFields.reduce((acc, { key }) => {
                // Set the value
                acc[key] = Number(data[key]) || 0;

                // Handle API typo for 'commission'
                let backendKey;
                if (key === "commission") {
                    backendKey = "commisionApplicable";
                } else if (key === "deliveryFeeperThousandperKm") {
                    // Special case for delivery fee applicable field
                    backendKey = "deliveryFeeperkmApplicable";
                } else {
                    backendKey = `${key}Applicable`;
                }

                const val = data[backendKey];
                acc[`${key}Applicable`] = val === true || val === "true" || val === 1;

                return acc;
            }, {
                id: data.id || 0,
                couponAmount: Number(data.couponAmount) || 0,
                distance: Number(data.distance) || 0
            });

            setBillingData(initializedData);
        } catch (error) {
            console.error("❌ Error fetching billing data:", error);
        } finally {
            hideLoader(); // Hide global loader when done
        }
    };

    const toggleEdit = (key) => {
        setEditMode((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleValueChange = (key, value) => {
        setBillingData((prev) => ({ ...prev, [key]: value }));
    };

    const handleCheckboxChange = (key) => {
        const applicableKey = `${key}Applicable`;
        setBillingData((prev) => ({
            ...prev,
            [applicableKey]: !prev[applicableKey],
        }));
    };

    const handleSaveOrUpdate = async (key) => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                alert("⚠️ Auth token missing");
                setLoading(false);
                return;
            }

            // Build payload dynamically with backend key fix
            const payload = {
                id: billingData.id || 0,
                couponAmount: Number(billingData.couponAmount) || 0,
                distance: Number(billingData.distance) || 0
            };

            billingFields.forEach(({ key }) => {
                payload[key] = Number(billingData[key]) || 0;

                // Fix backend typo for commissionApplicable and delivery fee
                let backendKey;
                if (key === "commission") {
                    backendKey = "commisionApplicable";
                } else if (key === "deliveryFeeperThousandperKm") {
                    // Special case for delivery fee applicable field
                    backendKey = "deliveryFeeperkmApplicable";
                } else {
                    backendKey = `${key}Applicable`;
                }

                payload[backendKey] = Boolean(billingData[`${key}Applicable`]);
            });

            const url =
                billingData.id && billingData.id !== 0
                    ? "https://backend.maamaas.com/catering/api/admin/billing/update"
                    : "https://backend.maamaas.com/catering/api/admin/billing/create";

            const method = billingData.id && billingData.id !== 0 ? "put" : "post";

            const response = await axios({
                method,
                url,
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                data: payload,
            });

            console.log("✅ Billing data saved/updated:", response.data);
            alert("Billing settings saved successfully!");

            // Close the edit mode for this key
            if (key) setEditMode((prev) => ({ ...prev, [key]: false }));
            else setEditMode({});

            // Refresh data from API
            fetchBillingData();
        } catch (error) {
            console.error(
                "❌ Error saving/updating billing data:",
                error.response?.data || error.message
            );
            alert(
                `Failed to save/update billing settings: ${error.response?.data?.message || error.message}`
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={containerStyle}>
            {/* Global Loader (same as Userdetails component) */}
            <Loader loading={globalLoading} fadeOut={fadeOut} text="Loading billing data..." />

            {/* Add loader animations to existing style */}
            <style>{`
                @keyframes letterAnim {
                    0% { opacity: 0; transform: translateY(30px); }
                    100% { opacity: 1; transform: translateY(0); }
                }

                @keyframes progress {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(250%); }
                }
            `}</style>

            {/* Table with Fixed Headers - IMPROVED VERSION */}
            <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                overflow: 'hidden',
                marginTop: '10px'
            }}>
                {/* Fixed Header - Separated from scrollable content */}
                <div style={{
                    backgroundColor: 'rgb(230 109 51)',
                    borderBottom: '2px solid rgb(230 109 51)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '500px 150px 100px 100px',
                        width: '100%'
                    }}>
                        <div style={{ ...fixedHeaderCellStyle }}>Charge Type</div>
                        <div style={{ ...fixedHeaderCellStyle }}>Percentage (%)</div>
                        <div style={{ ...fixedHeaderCellStyle }}>Visibility</div>
                        <div style={{ ...fixedHeaderCellStyle }}>Actions</div>
                    </div>
                </div>

                {/* Scrollable Body */}
                <div style={{
                    maxHeight: "500px",
                    overflowY: "auto",
                    backgroundColor: '#fff'
                }}>
                    {billingFields.map(({ label, key }, index) => (
                        <div
                            key={key}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '500px 150px 100px 100px',
                                width: '100%',
                                backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                                borderBottom: index < billingFields.length - 1 ? '1px solid #ddd' : 'none'
                            }}
                        >
                            {/* Charge Type */}
                            <div style={gridCellStyle}>
                                <strong>{label}</strong>
                            </div>

                            {/* Percentage */}
                            <div style={gridCellStyle}>
                                {editMode[key] ? (
                                    <input
                                        type="number"
                                        value={billingData[key] || ""}
                                        onChange={(e) => handleValueChange(key, e.target.value)}
                                        style={inputStyle}
                                    />
                                ) : (
                                    <span>{billingData[key] || "—"}</span>
                                )}
                            </div>

                            {/* Visibility */}
                            <div style={gridCellStyle}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(billingData[`${key}Applicable`])}
                                    disabled={!editMode[key]}
                                    onChange={() => handleCheckboxChange(key)}
                                />
                            </div>

                            {/* Actions */}
                            <div style={gridCellStyle}>
                                {editMode[key] ? (
                                    <FaSave
                                        onClick={() => handleSaveOrUpdate(key)}
                                        style={{ ...iconStyle, color: "#4caf50" }}
                                        title="Save"
                                    />
                                ) : (
                                    <FaEdit
                                        onClick={() => toggleEdit(key)}
                                        style={{ ...iconStyle }}
                                        title="Edit"
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <button
                    onClick={() => handleSaveOrUpdate()}
                    style={saveButtonStyle}
                    onMouseOver={(e) => (e.target.style.backgroundColor = "#45a049")}
                    onMouseOut={(e) => (e.target.style.backgroundColor = "#4CAF50")}
                >
                    Save All
                </button>
            </div>
        </div>
    );
};

// Styles
const containerStyle = { maxWidth: 1200, margin: "0 auto", fontFamily: "Segoe UI, sans-serif", padding: "0 20px" };

// Fixed header cell style
const fixedHeaderCellStyle = {
    padding: '12px',
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'center',
    borderRight: '1px solid rgba(255,255,255,0.2)',
    fontSize: '14px'
};

// Grid cell style for body rows
const gridCellStyle = {
    padding: '12px',
    textAlign: 'center',
    borderRight: '1px solid #ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50px'
};

const inputStyle = {
    width: '80px',
    padding: '5px',
    border: "1px solid #ccc",
    borderRadius: 5,
    textAlign: "center"
};

const iconStyle = {
    fontSize: 20,
    cursor: "pointer",
    margin: "0 5px"
};

const saveButtonStyle = {
    padding: "10px 20px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontWeight: "bold",
    transition: "background-color 0.3s",
};

export default Cateringbillingsetup;